import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (iso) => { try { return new Date(iso).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" }); } catch { return iso; } };

export const WeekReflection = ({ canReflect, refreshKey }) => {
  const [reflection, setReflection] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/journal/reflect/latest`).then((r) => setReflection(r.data.reflection)).catch(() => {});
  }, [refreshKey]);

  const reflect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/journal/reflect`);
      setReflection(r.data.reflection);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "The reflection didn't come through. Try once more.");
    } finally {
      setBusy(false);
    }
  };

  const fresh = reflection && Date.now() - new Date(reflection.created_at).getTime() < 7 * 86400000;

  return (
    <div className="mt-6 pt-6 border-t border-[#E7DFD5]" data-testid="journal-reflect">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#78716C] flex items-center gap-2"><Sparkles size={11} className="text-[#B45309]" /> Reflect on my week</div>
          <p className="text-xs text-[#A8A29E] mt-1 max-w-md">A short, gentle read of your last seven days — from your own notes. Written for you only; nobody else ever sees it.</p>
        </div>
        <button
          onClick={reflect}
          disabled={busy || !canReflect}
          title={canReflect ? "Write a reflection from this week's notes" : "Write one note or ground once this week first"}
          className="btn-outline text-xs whitespace-nowrap disabled:opacity-40"
          data-testid="journal-reflect-btn"
        >
          <Sparkles size={13} /> {busy ? "Reading your week…" : fresh ? "Reflect again" : "Reflect on my week"}
        </button>
      </div>
      <AnimatePresence>
        {reflection && (
          <motion.blockquote
            key={reflection.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.65, 0, 0.35, 1] }}
            className="mt-4 border-l-2 border-[#F59E0B]/60 pl-4 py-1"
            data-testid="journal-reflection"
          >
            <p className="font-display text-lg md:text-xl leading-relaxed text-[#44403C] whitespace-pre-wrap" data-testid="journal-reflection-text">{reflection.text}</p>
            <footer className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E] mt-3 flex items-center gap-2" data-testid="journal-reflection-meta">
              <Lock size={10} /> {when(reflection.created_at)} · {reflection.notes_count} note{reflection.notes_count === 1 ? "" : "s"} · {reflection.grounded_days} of 7 grounded · private
            </footer>
          </motion.blockquote>
        )}
      </AnimatePresence>
    </div>
  );
};
